import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { MessageCircle, Upload } from "lucide-react";
import type {
  CmmdAttachment,
  CmmdMember,
  CmmdTaskStatus,
  EnvironmentId,
  ThreadId,
} from "@cmmd-forge/contracts";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Sheet, SheetPopup } from "../ui/sheet";
import { cn } from "~/lib/utils";
import { TaskAttachmentList } from "./TaskAttachmentList";
import { WorkItemProjectIcon } from "./WorkItemProjectIcon";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import { formatDateInputValue, formatDueDate } from "./displayHelpers";
import {
  formatAssigneeDisplayName,
  formatPriorityDisplayLabel,
  formatStatusDisplayLabel,
} from "./taskRowFormatting";
import { toastManager } from "../ui/toast";
import { cmmdWorkIntakeResource, readCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import {
  buildAssigneePatch,
  buildDependencyRowModel,
  buildDescriptionPatch,
  buildDueDatePatch,
  buildLabelsPatch,
  buildPriorityPatch,
  buildTitlePatch,
  canUpdateTaskDetails,
  canUpdateTaskStatus,
  getAttachmentUploadSuccessTitle,
  isCmmdTaskItem,
  inlineFieldClass,
  normalizeTaskStatus,
  parseTaskLabels,
  resolveActiveThreadRouteParams,
  resolveCmmdTaskId,
  resolveStatusBadgeVariant,
  NO_RELATED_THREADS,
  SAVE_RING_CLASS,
  type InlineSaveField,
  type InlineSaveState,
  type PanelTab,
} from "./TaskDetailPanel.logic";
import { TaskDetailCommentRow } from "./TaskDetailCommentRow";
import { useTaskDetailComments } from "./useTaskDetailComments";
import { TaskDetailStatusProperty } from "./TaskDetailStatusProperty";

export interface DependencySummary {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly done: boolean;
}

export interface TaskMutationPatch {
  readonly status?: CmmdTaskStatus;
  readonly priority?: string | null;
  readonly title?: string;
  readonly description?: string | null;
  readonly dueDate?: string | null;
  readonly assigneeId?: string | null;
  readonly labels?: ReadonlyArray<string>;
}

export interface TaskDetailThreadSummary {
  readonly id: ThreadId;
  readonly environmentId: EnvironmentId;
  readonly title: string;
  readonly status: string;
  readonly provider: string | null;
  readonly updatedAt: string | null;
  readonly latestUserMessageAt: string | null;
}

interface TaskDetailPanelProps {
  item: WorkItem | null;
  assigneeId?: string | null;
  description: string | null;
  relatedThreads?: ReadonlyArray<TaskDetailThreadSummary>;
  /**
   * Resolver for dependency ids → summaries. Returns null for ids that
   * aren't in the currently-loaded work set (e.g. archived tasks). The
   * panel renders unresolved ids as a bare "unknown" row.
   */
  resolveDependency?: (id: string) => DependencySummary | null;
  onClose: () => void;
  onStartThread: (itemId: string) => void | Promise<void>;
  onUpdateTask?: (itemId: string, patch: TaskMutationPatch) => void | Promise<void>;
  /** Called when the user clicks a blocker row — lets the caller navigate/select it. */
  onNavigateToDependency?: (id: string) => void;
}

function PropertyRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="group grid grid-cols-[96px_minmax(0,1fr)] items-center gap-3 border-b border-border/60 py-2.5 last:border-0">
      <span className="text-xs font-medium text-muted-foreground/62">{label}</span>
      <div className="min-w-0 text-sm text-muted-foreground group-hover:text-foreground/86">
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  return (
    <Badge variant={resolveStatusBadgeVariant(status)} className="text-[11px] font-semibold">
      {formatStatusDisplayLabel(status)}
    </Badge>
  );
}

export function TaskDetailPanel({
  item,
  assigneeId = null,
  description,
  relatedThreads = NO_RELATED_THREADS,
  onClose,
  onStartThread,
  onUpdateTask,
  resolveDependency,
  onNavigateToDependency,
}: TaskDetailPanelProps) {
  const [tab, setTab] = useState<PanelTab>("details");
  const [draftStatus, setDraftStatus] = useState<CmmdTaskStatus>("todo");
  const [draftPriority, setDraftPriority] = useState<string>("none");
  const [draftDueDate, setDraftDueDate] = useState<string>("");
  const [draftAssigneeId, setDraftAssigneeId] = useState<string>("");
  const [fieldSaveState, setFieldSaveState] = useState<
    Partial<Record<InlineSaveField, InlineSaveState>>
  >({});
  const [existingAttachments, setExistingAttachments] = useState<ReadonlyArray<CmmdAttachment>>([]);
  const [sessionAttachments, setSessionAttachments] = useState<ReadonlyArray<CmmdAttachment>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [members, setMembers] = useState<ReadonlyArray<CmmdMember>>([]);
  const [isStartingThread, setIsStartingThread] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftLabels, setDraftLabels] = useState("");
  const [editingDescription, setEditingDescription] = useState(false);
  const [draftDescription, setDraftDescription] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const saveStateTimersRef = useRef<Partial<Record<InlineSaveField, number>>>({});
  const navigate = useNavigate();
  const canUpdateTask = canUpdateTaskDetails(item);
  const canUpdateStatus = canUpdateTaskStatus(item);
  const isCmmdTask = isCmmdTaskItem(item);
  const cmmdTaskId = resolveCmmdTaskId(item);
  const cmmdTaskNumber = cmmdTaskId ? Number(cmmdTaskId.replace(/^task-/, "")) : null;
  const prevAgentStatusRef = useRef<string | null>(null);
  const activeCmmdTaskIdRef = useRef(cmmdTaskId);
  activeCmmdTaskIdRef.current = cmmdTaskId;
  const taskComments = useTaskDetailComments(cmmdTaskId, cmmdTaskNumber);

  useEffect(() => {
    if (!item) return;
    setDraftStatus(normalizeTaskStatus(item.status));
    setDraftPriority(item.priority ?? "none");
    setDraftDueDate(formatDateInputValue(item.dueDate));
    setDraftAssigneeId(assigneeId ?? "");
    setDraftTitle(item.title ?? "");
    const tags = (item as unknown as { tags?: string[] }).tags ?? [];
    setDraftLabels(tags.join(", "));
    setFieldSaveState({});
  }, [assigneeId, item]);

  useEffect(
    () => () => {
      for (const timeoutId of Object.values(saveStateTimersRef.current)) {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      }
    },
    [],
  );

  // When an agent turn completes for this CMMD task, trigger a CMMD store
  // refresh so the panel reflects the new status synced by the server.
  useEffect(() => {
    const currentAgentStatus = item?.agentStatus ?? null;
    const prevAgentStatus = prevAgentStatusRef.current;
    prevAgentStatusRef.current = currentAgentStatus;
    if (
      prevAgentStatus === "running" &&
      currentAgentStatus !== "running" &&
      item?.source === "cmmd-task"
    ) {
      cmmdWorkIntakeResource.requestRefetch();
    }
  }, [item?.agentStatus, item?.source]);

  // Fetch attachments + members when a CMMD task panel opens.
  useEffect(() => {
    setExistingAttachments([]);
    setSessionAttachments([]);
    if (!cmmdTaskId) return;
    let cancelled = false;
    const workspaceId = readCmmdWorkIntakeSnapshot().workspace?.id;
    Promise.all([
      cmmdApiClient.getTaskAttachments(cmmdTaskId),
      workspaceId ? cmmdApiClient.getWorkspaceMembers(workspaceId) : Promise.resolve([]),
    ])
      .then(([attachmentData, memberData]) => {
        if (!cancelled) {
          setExistingAttachments(attachmentData ?? []);
          setMembers(memberData ?? []);
        }
      })
      .catch(() => {
        // 503 if JWT not yet issued; silently degrade
      })
      .finally(() => {});
    return () => {
      cancelled = true;
    };
  }, [cmmdTaskId]);

  const taskSheetClassName =
    "h-dvh w-full max-w-none overflow-hidden rounded-none border-0 bg-card p-0 text-card-foreground shadow-2xl shadow-black/35 sm:my-3 sm:mr-3 sm:h-[calc(100dvh-1.5rem)] sm:w-[min(560px,calc(100vw-2rem))] sm:rounded-lg sm:border sm:border-border";

  if (!item) {
    return (
      <Sheet open={false} onOpenChange={(open) => !open && onClose()}>
        <SheetPopup
          side="right"
          showCloseButton={false}
          variant="inset"
          className={taskSheetClassName}
        />
      </Sheet>
    );
  }

  const hasActiveThread = !!item.threadId;
  const formattedDue = formatDueDate(item.dueDate);
  const parsedLabels = parseTaskLabels(draftLabels);
  const handleAttachmentFiles = (files: FileList | readonly File[]) => {
    if (!cmmdTaskId || files.length === 0) return;
    const filesToUpload = Array.from(files);
    setIsUploading(true);
    Promise.all(filesToUpload.map((file) => cmmdApiClient.uploadTaskAttachment(cmmdTaskId, file)))
      .then((attachments) => {
        setSessionAttachments((prev) => [...prev, ...attachments]);
        setTab("attachments");
        toastManager.add({
          type: "success",
          title: getAttachmentUploadSuccessTitle(filesToUpload.length),
        });
      })
      .catch((err: unknown) => {
        toastManager.add({
          type: "error",
          title: "Upload failed",
          description: err instanceof Error ? err.message : undefined,
        });
      })
      .finally(() => setIsUploading(false));
  };
  const handleAttachmentPaste = (event: ClipboardEvent<HTMLElement>) => {
    if (!isCmmdTask) return;
    const imageFiles = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (imageFiles.length === 0) return;
    event.preventDefault();
    handleAttachmentFiles(imageFiles);
  };

  const saveInlineField = (field: InlineSaveField, patch: TaskMutationPatch) => {
    if (!onUpdateTask) return;
    const previousTimer = saveStateTimersRef.current[field];
    if (previousTimer !== undefined) window.clearTimeout(previousTimer);
    setFieldSaveState((current) => ({ ...current, [field]: "saving" }));
    void Promise.resolve(onUpdateTask(item.id, patch))
      .then(() => {
        setFieldSaveState((current) => ({ ...current, [field]: "saved" }));
        saveStateTimersRef.current[field] = window.setTimeout(() => {
          setFieldSaveState((current) => ({ ...current, [field]: "idle" }));
        }, 900);
      })
      .catch((error: unknown) => {
        setFieldSaveState((current) => ({ ...current, [field]: "error" }));
        toastManager.add({
          type: "error",
          title: "Failed to update task",
          description: error instanceof Error ? error.message : undefined,
        });
      });
  };
  const getFieldSaveState = (field: InlineSaveField): InlineSaveState =>
    fieldSaveState[field] ?? "idle";
  const commitLabels = () => {
    const patch = buildLabelsPatch(item, draftLabels);
    if (!patch) return;
    saveInlineField("tags", patch);
  };
  return (
    <Sheet open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetPopup
        side="right"
        showCloseButton={false}
        variant="inset"
        className={taskSheetClassName}
      >
        <aside
          data-testid="command-center-detail-panel"
          className={cn(
            "relative flex min-h-0 flex-1 flex-col transition-colors",
            isDraggingOver && "bg-hue-sky-surface",
          )}
          onDragEnter={(event) => {
            if (!isCmmdTask || !Array.from(event.dataTransfer.types).includes("Files")) return;
            event.preventDefault();
            dragDepthRef.current += 1;
            setIsDraggingOver(true);
          }}
          onDragOver={(event) => {
            if (!isCmmdTask || !Array.from(event.dataTransfer.types).includes("Files")) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
            setIsDraggingOver(true);
          }}
          onDragLeave={(event) => {
            if (!isCmmdTask || !Array.from(event.dataTransfer.types).includes("Files")) return;
            event.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setIsDraggingOver(false);
          }}
          onDrop={(event) => {
            if (!isCmmdTask) return;
            event.preventDefault();
            dragDepthRef.current = 0;
            setIsDraggingOver(false);
            handleAttachmentFiles(event.dataTransfer.files);
          }}
          onPaste={handleAttachmentPaste}
        >
          {isDraggingOver ? (
            <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border border-hue-sky-border bg-hue-sky-surface shadow-[inset_0_0_0_1px_rgba(56,189,248,0.25)] backdrop-blur-[1px] dark:bg-hue-sky/45">
              <div className="flex flex-col items-center text-center">
                <div className="mb-3 flex size-11 items-center justify-center rounded-full border border-hue-sky-border bg-hue-sky-surface text-hue-sky-foreground">
                  <Upload className="size-5" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold text-hue-sky-foreground">Drop to attach</p>
                <p className="mt-1 text-xs text-hue-sky-foreground">
                  Files will be uploaded to this CMMD task.
                </p>
              </div>
            </div>
          ) : null}
          {/* Header: status, title, close */}
          <div className="border-b border-border px-4 pt-3 pb-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <StatusBadge status={item.status} />
              <button
                type="button"
                aria-label="Close detail panel"
                title="Close (Esc)"
                onClick={onClose}
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/65 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                ✕
              </button>
            </div>
            {editingTitle && isCmmdTask ? (
              <input
                data-testid="inline-title-editor"
                autoFocus
                type="text"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.currentTarget.value)}
                onBlur={() => {
                  setEditingTitle(false);
                  const patch = buildTitlePatch({
                    currentTitle: item.title,
                    draftTitle,
                    cmmdTaskId,
                  });
                  if (!patch) return;
                  saveInlineField("title", patch);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") {
                    setDraftTitle(item.title ?? "");
                    setEditingTitle(false);
                  }
                }}
                className={cn(
                  "w-full rounded border border-border bg-background px-1.5 py-0.5 text-base font-semibold leading-snug tracking-tight outline-none focus:border-border/80",
                  SAVE_RING_CLASS[getFieldSaveState("title")],
                )}
              />
            ) : (
              <h2
                data-testid="task-title"
                className={cn(
                  "text-base font-semibold leading-snug tracking-tight",
                  isCmmdTask &&
                    "cursor-text rounded hover:bg-muted/40 px-1 -ml-1 transition-colors",
                )}
                title={isCmmdTask ? "Click to edit title" : undefined}
                onClick={() => {
                  if (!isCmmdTask) return;
                  setDraftTitle(item.title ?? "");
                  setEditingTitle(true);
                }}
              >
                {item.title}
              </h2>
            )}
            <div className="mt-3">
              {hasActiveThread ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full justify-center text-xs"
                  onClick={() => {
                    const params = resolveActiveThreadRouteParams(item);
                    if (params) {
                      void navigate({
                        to: "/$environmentId/$threadId",
                        params,
                      });
                    }
                  }}
                >
                  Open active thread
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-8 w-full justify-center text-xs"
                  onClick={() => onStartThread(item.id)}
                >
                  Start thread
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            {(["details", "threads", "comments", "attachments"] as PanelTab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  "flex-1 h-10 text-xs border-b-[1.5px] border-b-transparent -mb-px transition-colors",
                  tab === t
                    ? "text-foreground border-b-foreground/50"
                    : "text-muted-foreground/50 hover:text-muted-foreground/80",
                )}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {tab === "details" && (
              <div className="flex-1 overflow-y-auto">
                {(description || isCmmdTask) && (
                  <div className="px-4 pt-3 pb-2">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/55">
                      Description
                    </div>
                    {editingDescription && isCmmdTask ? (
                      <textarea
                        data-testid="inline-description-editor"
                        autoFocus
                        rows={4}
                        value={draftDescription}
                        onChange={(e) => setDraftDescription(e.currentTarget.value)}
                        onBlur={() => {
                          setEditingDescription(false);
                          const patch = buildDescriptionPatch({
                            currentDescription: description,
                            draftDescription,
                            cmmdTaskId,
                          });
                          if (!patch) return;
                          saveInlineField("description", patch);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setDraftDescription(description ?? "");
                            setEditingDescription(false);
                          }
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                            e.currentTarget.blur();
                          }
                        }}
                        className={cn(
                          "w-full rounded border border-border bg-background px-1.5 py-1 text-sm leading-relaxed text-foreground outline-none focus:border-border/80 resize-none",
                          SAVE_RING_CLASS[getFieldSaveState("description")],
                        )}
                        placeholder="Add a description…"
                      />
                    ) : (
                      <p
                        data-testid="task-description"
                        className={cn(
                          "text-sm leading-relaxed text-muted-foreground min-h-[1.5rem]",
                          SAVE_RING_CLASS[getFieldSaveState("description")],
                          isCmmdTask &&
                            "cursor-text rounded hover:bg-muted/40 px-1 -mx-1 transition-colors",
                        )}
                        title={isCmmdTask ? "Click to edit description" : undefined}
                        onClick={() => {
                          if (!isCmmdTask) return;
                          setDraftDescription(description ?? "");
                          setEditingDescription(true);
                        }}
                      >
                        {description ||
                          (isCmmdTask && (
                            <span className="text-muted-foreground/35 italic">
                              Add a description…
                            </span>
                          ))}
                      </p>
                    )}
                  </div>
                )}
                <div className="px-4 py-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/55">
                      Properties
                    </div>
                  </div>
                  <div className="rounded-lg border border-border/45 bg-background/12 px-3">
                    <TaskDetailStatusProperty
                      status={item.status}
                      draftStatus={draftStatus}
                      editable={canUpdateStatus && onUpdateTask != null}
                      className={inlineFieldClass(getFieldSaveState("status"), "w-full")}
                      onChange={(nextStatus) => {
                        setDraftStatus(nextStatus);
                        if (nextStatus !== item.status) {
                          saveInlineField("status", { status: nextStatus });
                        }
                      }}
                    />
                    <PropertyRow
                      label="Priority"
                      value={
                        canUpdateTask && onUpdateTask ? (
                          <select
                            value={draftPriority}
                            onChange={(event) => {
                              const nextPriority = event.currentTarget.value;
                              setDraftPriority(nextPriority);
                              const patch = buildPriorityPatch({
                                currentPriority: item.priority,
                                nextPriority,
                              });
                              if (patch) saveInlineField("priority", patch);
                            }}
                            className={inlineFieldClass(getFieldSaveState("priority"), "w-full")}
                          >
                            <option value="none">None</option>
                            <option value="urgent">Urgent</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        ) : (
                          formatPriorityDisplayLabel(item.priority)
                        )
                      }
                    />
                    <PropertyRow
                      label="Project"
                      value={
                        <span className="flex min-w-0 items-center gap-1.5 font-medium">
                          <WorkItemProjectIcon
                            projectName={item.projectName}
                            projectIcon={item.projectIcon}
                            projectIconUrl={item.projectIconUrl}
                          />
                          <span className="truncate">{item.projectName}</span>
                        </span>
                      }
                    />
                    <PropertyRow label="Campaign" value={item.campaignName ?? "—"} />
                    <PropertyRow
                      label="Due"
                      value={
                        canUpdateTask && onUpdateTask ? (
                          <input
                            type="date"
                            value={draftDueDate}
                            onChange={(event) => {
                              const nextDueDate = event.currentTarget.value;
                              setDraftDueDate(nextDueDate);
                              const patch = buildDueDatePatch({
                                currentDueDateInput: formatDateInputValue(item.dueDate),
                                nextDueDate,
                              });
                              if (patch) saveInlineField("dueDate", patch);
                            }}
                            className={inlineFieldClass(getFieldSaveState("dueDate"), "w-full")}
                          />
                        ) : (
                          (formattedDue?.label ?? "—")
                        )
                      }
                    />
                    <PropertyRow
                      label="Assignee"
                      value={
                        canUpdateTask && onUpdateTask ? (
                          members.length > 0 ? (
                            <select
                              value={draftAssigneeId}
                              onChange={(event) => {
                                const nextAssigneeId = event.currentTarget.value;
                                setDraftAssigneeId(nextAssigneeId);
                                const patch = buildAssigneePatch({
                                  currentAssigneeId: assigneeId,
                                  nextAssigneeId,
                                });
                                if (patch) saveInlineField("assignee", patch);
                              }}
                              className={inlineFieldClass(getFieldSaveState("assignee"), "w-full")}
                            >
                              <option value="">Unassigned</option>
                              {members.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="inline-flex h-7 max-w-full items-center rounded-md border border-transparent px-2 text-sm font-medium text-muted-foreground/55">
                              Members unavailable
                            </span>
                          )
                        ) : (
                          formatAssigneeDisplayName(assigneeId, members)
                        )
                      }
                    />
                    <PropertyRow
                      label="Agent"
                      value={
                        item.agentStatus === "running" ? (
                          <span className="font-medium text-success-foreground">
                            {item.agentProvider ?? "Running"}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/45">None</span>
                        )
                      }
                    />
                    {isCmmdTask ? (
                      <PropertyRow
                        label="Tags"
                        value={
                          canUpdateTask && onUpdateTask ? (
                            <input
                              type="text"
                              value={draftLabels}
                              onChange={(event) => setDraftLabels(event.currentTarget.value)}
                              onBlur={commitLabels}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") event.currentTarget.blur();
                                if (event.key === "Escape") {
                                  const tags = (item as unknown as { tags?: string[] }).tags ?? [];
                                  setDraftLabels(tags.join(", "));
                                  event.currentTarget.blur();
                                }
                              }}
                              className={inlineFieldClass(getFieldSaveState("tags"), "w-full")}
                              placeholder="bug, priority, ux"
                            />
                          ) : parsedLabels.length > 0 ? (
                            parsedLabels.join(", ")
                          ) : (
                            "—"
                          )
                        }
                      />
                    ) : null}
                  </div>
                </div>
                {item.dependsOn.length > 0 && (
                  <div className="border-b border-border px-4 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/55">
                        Dependencies
                      </div>
                      <span
                        className={cn(
                          "text-[11px] tabular-nums",
                          item.blockedByCount > 0
                            ? "text-destructive-foreground"
                            : "text-success-foreground",
                        )}
                      >
                        {item.blockedByCount > 0
                          ? `${item.blockedByCount} blocking`
                          : "All complete"}
                      </span>
                    </div>
                    <ul className="flex flex-col gap-1">
                      {item.dependsOn.map((depId) => {
                        const dependencyRow = buildDependencyRowModel(
                          depId,
                          resolveDependency?.(depId) ?? null,
                          !!onNavigateToDependency,
                        );
                        return (
                          <li key={depId}>
                            <button
                              type="button"
                              onClick={() => onNavigateToDependency?.(depId)}
                              disabled={!dependencyRow.isNavigable}
                              className={cn(
                                "flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors",
                                "disabled:cursor-default",
                                dependencyRow.isDone
                                  ? "border-border/50 bg-muted/30 text-muted-foreground/50"
                                  : "border-border hover:bg-muted hover:border-border/80",
                              )}
                            >
                              <span
                                aria-hidden
                                className={cn(
                                  "inline-block size-1.5 shrink-0 rounded-full",
                                  dependencyRow.isDone ? "bg-success/60" : "bg-destructive",
                                )}
                              />
                              <span
                                className={cn("truncate", dependencyRow.isDone && "line-through")}
                              >
                                {dependencyRow.title}
                              </span>
                              <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground/50">
                                {dependencyRow.statusLabel}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {tab === "threads" && (
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <div className="mb-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/55">
                      Threads
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground/50">
                      Forge work history linked to this task.
                    </p>
                  </div>
                </div>
                {relatedThreads.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/55 px-4 py-8 text-center">
                    <p className="text-sm font-medium text-muted-foreground/75">
                      No threads have worked this task yet.
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground/45">
                      Start a thread to attach Forge execution history to it.
                    </p>
                  </div>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {relatedThreads.map((thread) => {
                      const activityAt = thread.latestUserMessageAt ?? thread.updatedAt;
                      return (
                        <li key={`${thread.environmentId}:${thread.id}`}>
                          <button
                            type="button"
                            onClick={() => {
                              void navigate({
                                to: "/$environmentId/$threadId",
                                params: {
                                  environmentId: thread.environmentId,
                                  threadId: thread.id,
                                },
                              });
                            }}
                            className="group w-full rounded-lg border border-border/55 bg-background/20 px-3 py-2.5 text-left transition-colors hover:border-border hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <div className="flex min-w-0 items-center justify-between gap-3">
                              <span className="truncate text-sm font-semibold text-foreground">
                                {thread.title}
                              </span>
                              <span className="shrink-0 rounded border border-border/55 px-1.5 py-0.5 text-[11px] font-medium capitalize text-muted-foreground/70">
                                {thread.status.replace("-", " ")}
                              </span>
                            </div>
                            <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground/50">
                              {thread.provider ? <span>{thread.provider}</span> : null}
                              {thread.provider && activityAt ? <span aria-hidden>·</span> : null}
                              {activityAt ? (
                                <span>{formatRelativeTimeLabel(activityAt)}</span>
                              ) : null}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {tab === "comments" && (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {/* Comment list */}
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  {taskComments.loading ? (
                    <p className="text-center text-xs text-muted-foreground/50">Loading…</p>
                  ) : taskComments.comments.length === 0 ? (
                    <div className="flex h-full min-h-[260px] items-center justify-center">
                      <div className="flex max-w-52 flex-col items-center text-center">
                        <div className="mb-3 flex size-10 items-center justify-center rounded-full border border-border/65 bg-muted/20 text-muted-foreground/55">
                          <MessageCircle className="size-4" aria-hidden="true" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground/80">
                          {isCmmdTask ? "No comments yet" : "Comments unavailable"}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground/45">
                          {isCmmdTask ? "Be the first!" : "This item does not support comments."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {taskComments.comments.map((c) => (
                        <TaskDetailCommentRow
                          key={c.id}
                          comment={c}
                          editing={taskComments.editingId === c.id}
                          deleting={taskComments.deletingId === c.id}
                          draft={taskComments.editingDraft}
                          onBeginEdit={() => {
                            taskComments.beginEdit(c);
                          }}
                          onDraftChange={taskComments.setEditingDraft}
                          onCancel={() => taskComments.cancelEdit()}
                          onDelete={() => taskComments.deleteComment(c)}
                          onSave={() => taskComments.saveEdit(c)}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {/* Compose (CMMD tasks only) */}
                {isCmmdTask && (
                  <div className="border-t border-border px-4 py-3">
                    <textarea
                      value={taskComments.draft}
                      onChange={(e) => taskComments.setDraft(e.currentTarget.value)}
                      placeholder="Write a comment…"
                      rows={2}
                      className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-border/80"
                    />
                    <div className="mt-1.5 flex justify-end">
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        data-testid="comment-post-button"
                        disabled={taskComments.posting}
                        onClick={taskComments.post}
                      >
                        {taskComments.posting ? "Posting…" : "Post"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "attachments" && (
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/55">
                      Attachments
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground/50">
                      Files linked to this CMMD task.
                    </p>
                  </div>
                  {isCmmdTask ? (
                    <button
                      type="button"
                      disabled={isUploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex h-7 items-center rounded-md border border-border/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                    >
                      {isUploading ? "Uploading…" : "Attach"}
                    </button>
                  ) : null}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const files = e.currentTarget.files;
                    if (files) handleAttachmentFiles(files);
                    e.currentTarget.value = "";
                  }}
                />
                {existingAttachments.length === 0 && sessionAttachments.length === 0 ? (
                  <div
                    className={cn(
                      "flex min-h-[160px] flex-col items-center justify-center rounded-lg border border-dashed border-border/55 px-4 py-8 text-center transition-colors",
                      isUploading && "border-hue-sky-border bg-hue-sky-surface",
                    )}
                  >
                    <div className="mb-3 flex size-10 items-center justify-center rounded-full border border-border/65 bg-muted/20 text-muted-foreground/55">
                      <Upload className="size-4" aria-hidden="true" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground/80">
                      {isUploading ? "Uploading attachment…" : "No attachments"}
                    </p>
                    <p className="mt-1 max-w-64 text-xs leading-5 text-muted-foreground/45">
                      Drop files anywhere on this task, or use Attach.
                    </p>
                  </div>
                ) : (
                  <TaskAttachmentList
                    attachments={[...existingAttachments, ...sessionAttachments]}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer action */}
          {!hasActiveThread && (
            <>
              <div className="border-t border-border" />
              <div className="px-4 py-3">
                <Button
                  className="w-full"
                  size="sm"
                  disabled={isStartingThread}
                  aria-busy={isStartingThread}
                  onClick={() => {
                    setIsStartingThread(true);
                    void Promise.resolve(onStartThread(item.id)).finally(() => {
                      setIsStartingThread(false);
                    });
                  }}
                >
                  {isStartingThread ? "Starting thread..." : "Start a thread"}
                </Button>
              </div>
            </>
          )}
        </aside>
      </SheetPopup>
    </Sheet>
  );
}
